import React, { createContext, useCallback, useContext, useState } from 'react'
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react'

type ToastType = 'error' | 'success' | 'info'

interface Toast {
  id: number
  message: string
  type: ToastType
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

// Counter modul-level untuk id unik (tidak butuh Math.random).
let counter = 0

const STYLE: Record<ToastType, { text: string; bg: string; border: string; Icon: typeof AlertTriangle }> = {
  error:   { text: '#DC2626', bg: '#FEF2F2', border: '#FECACA', Icon: AlertTriangle },
  success: { text: '#15803D', bg: '#DCFCE7', border: '#BBF7D0', Icon: CheckCircle2 },
  info:    { text: '#4338CA', bg: '#EEF2FF', border: '#C7D2FE', Icon: Info },
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (message: string, type: ToastType = 'error') => {
      const id = ++counter
      setToasts((prev) => [...prev, { id, message, type }])
      setTimeout(() => remove(id), 5000)
    },
    [remove],
  )

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}

      {/* Stack toast — fixed kanan-bawah */}
      <div
        className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-[min(360px,calc(100vw-2rem))] pointer-events-none"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((t) => {
          const s = STYLE[t.type]
          return (
            <div
              key={t.id}
              role="alert"
              className="pointer-events-auto flex items-start gap-2.5 px-3.5 py-3 rounded-xl border shadow-lg shadow-black/[0.08] animate-slide-up bg-white"
              style={{ borderColor: s.border }}
            >
              <span
                className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: s.bg }}
              >
                <s.Icon className="w-3.5 h-3.5" style={{ color: s.text }} aria-hidden="true" />
              </span>
              <p className="flex-1 text-[13px] font-body text-[#1A1916] leading-snug pt-0.5">
                {t.message}
              </p>
              <button
                onClick={() => remove(t.id)}
                aria-label="Tutup notifikasi"
                className="p-1 -mr-1 -mt-0.5 rounded-lg text-[#AEADA8] hover:text-[#1A1916] hover:bg-[#F2F1EC] transition-colors flex-shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
